import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import { Card, CardHeader, Grid } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';

import { FeedKey } from 'api';
import { EmptyState, Layouts, LeaderboardWidget } from 'components';
import { Feed, FeedSelect } from 'features/feed';
import { DEFAULT_SORT } from 'constants/index';
import { useClient, useCurrentUser, useFeatures } from 'hooks';
import { isAdminUser } from 'helpers';
import * as paths from 'paths';

import {
  FeedMenu,
  RecentActivity,
  RecommendedGroups,
  RecommendedPeople,
  ShareThese,
  TotalReach,
} from '../components';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      marginBottom: theme.spacing(3),
    },
    header: {
      padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
      background: theme.palette.secondary.main,
      color: theme.palette.common.white,
    },
  });

interface IProps extends WithStyles<typeof styles> {}

const AllPosts: FC<IProps> = ({ classes }) => {
  const [sort, setSort] = useState(DEFAULT_SORT);
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const client = useClient();
  const { leaderboard } = useFeatures();

  if (!isAdminUser(currentUser)) {
    return <Redirect to={paths.index} />;
  }

  function renderRightSidebar() {
    return (
      <Grid container spacing={3} direction="column">
        <Grid item xs>
          <TotalReach />
        </Grid>
        {leaderboard && (
          <Grid item xs>
            <LeaderboardWidget variables={{ user_id: currentUser.id }} />
          </Grid>
        )}
        <Grid item xs>
          <ShareThese />
        </Grid>
        <Grid item xs>
          <RecentActivity />
        </Grid>
        <Grid item xs>
          <RecommendedPeople />
        </Grid>
        <Grid item xs>
          <RecommendedGroups />
        </Grid>
      </Grid>
    );
  }

  function renderMenu() {
    return <FeedMenu sort={sort} onSort={() => setSort(!sort)} />;
  }

  function renderMain() {
    const variables = {
      sort,
      key: FeedKey.Firehose,
      id: client.id,
    };

    return (
      <Layouts.AppMain2Column
        hideRightOnMobile
        desktopSplit="8/4"
        left={
          <Feed
            emptyState={() => <EmptyState name="timeline" />}
            variables={variables}
            header={renderHeader()}
          />
        }
        right={renderRightSidebar()}
      />
    );
  }

  function renderHeader() {
    return (
      <Card className={classes.root}>
        <CardHeader
          className={classes.header}
          title={t('admin:allPosts')}
          titleTypographyProps={{ variant: 'subtitle2', color: 'inherit' }}
          action={renderMenu()}
        />
      </Card>
    );
  }

  return <Layouts.App2Column sidebar={<FeedSelect />} main={renderMain()} />;
};

export default withStyles(styles)(AllPosts);
